import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { MetricsHeader } from '@/components/webinar/metrics/MetricsHeader';
import { MetricsTabs } from '@/components/webinar/metrics/MetricsTabs';
import { MetricsOverview } from '@/components/webinar/metrics/MetricsOverview';
import { MetricsRetention } from '@/components/webinar/metrics/MetricsRetention';
import { MetricsTraffic } from '@/components/webinar/metrics/MetricsTraffic';
import { MetricsFunnelSidebar } from '@/components/webinar/metrics/MetricsFunnelSidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type TabType = 'overview' | 'retention' | 'traffic' | 'devices' | 'regional';

export default function WebinarMetrics() {
  useLanguage();
  const navigate = useNavigate();
  const [selectedWebinar, setSelectedWebinar] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  
  // Mock: check if there are webinars
  const hasWebinars = false; // TODO: Replace with actual data

  const handleExport = () => {
    console.log('webinar_metrics.export');
    // TODO: Implement CSV export
  };

  const handleWebinarChange = (webinarId: string) => {
    setSelectedWebinar(webinarId);
    console.log('webinar_metrics.change_webinar', webinarId);
  };

  const handleDateChange = (range: { from: Date; to: Date }) => {
    setDateRange(range);
    console.log('webinar_metrics.change_date', range);
  };

  // Empty state
  if (!hasWebinars) {
    return (
      <Layout fixedSidebar>
        <div className="container mx-auto px-6 py-8">
          <h1 className="text-3xl font-bold mb-6">{translateInterfaceText("Métricas do Webinário")}</h1>
          
          <Card className="p-12 text-center">
            <div className="max-w-md mx-auto space-y-4">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                <Plus className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold">{translateInterfaceText("Você ainda não tem webinários")}</h3>
              <p className="text-muted-foreground">
                {translateInterfaceText("Crie seu primeiro webinário para começar a acompanhar métricas detalhadas de desempenho.")}</p>
              <Button onClick={() => navigate('/webinarios/criar')} size="lg">
                <Plus className="h-4 w-4 mr-2" />
                {translateInterfaceText("Criar Webinário")}</Button>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout fixedSidebar>
      <div className="container mx-auto px-6 py-8">
        <MetricsHeader
          selectedWebinar={selectedWebinar}
          dateRange={dateRange}
          onWebinarChange={handleWebinarChange}
          onDateChange={handleDateChange}
          onExport={handleExport}
        />

        <MetricsTabs activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6 mt-6">
          <div>
            {activeTab === 'overview' && <MetricsOverview />}
            {activeTab === 'retention' && <MetricsRetention />}
            {activeTab === 'traffic' && <MetricsTraffic />}
            {activeTab === 'devices' && (
              <Card className="p-8 text-center text-muted-foreground">
                {translateInterfaceText("Em desenvolvimento")}</Card>
            )}
            {activeTab === 'regional' && (
              <Card className="p-8 text-center text-muted-foreground">
                {translateInterfaceText("Em desenvolvimento")}</Card>
            )}
          </div>

          {/* Sidebar - visible only on xl screens */}
          <div className="hidden xl:block">
            <MetricsFunnelSidebar />
          </div>
        </div>
      </div>
    </Layout>
  );
}
